import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { IconButton, Tooltip } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import MapIcon from '@mui/icons-material/Map';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import MapScale from '../../map/MapScale';
import { map } from '../../map/core/MapView';
import { useTranslation } from '../../common/components/LocalizationProvider';

const ReportMapPanel = ({
  title,
  children,
  show,
  onClearSelection,
  isTouchViewport,
  collapsedHeight,
  collapsedMinHeight,
  collapsedMaxHeight,
  expandedHeight,
  expandedMinHeight,
  expandedMaxHeight,
  expandedInset,
  expandedTopOffset,
  expandedLeftOffset,
  expandedRightOffset,
  expandedBottomOffset,
  normalRadius,
  expandedRadius,
  zIndex,
  onExpandedChange,
  collapseSignal,
}) => {
  const t = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const bodyScrollPositionRef = useRef(0);
  const previousViewportTypeRef = useRef(null);
  const panelRef = useRef(null);

  const isPhone = useMediaQuery('(max-width:599px)');
  const isTablet = useMediaQuery('(min-width:600px) and (max-width:1023px)');
  const isTv = useMediaQuery('(min-width:1600px)');

  const viewportConfig = useMemo(() => {
    if (isPhone) {
      return {
        collapsedHeight: 'clamp(160px, 26vh, 220px)',
        collapsedMinHeight: 160,
        collapsedMaxHeight: 220,
        expandedHeight: 'calc(100dvh - 16px)',
        expandedMinHeight: 'calc(100dvh - 16px)',
        expandedMaxHeight: 'calc(100dvh - 16px)',
        expandedInset: 8,
        normalRadius: 12,
        expandedRadius: 14,
      };
    }

    if (isTablet) {
      return {
        collapsedHeight: 'clamp(220px, 30vh, 320px)',
        collapsedMinHeight: 220,
        collapsedMaxHeight: 320,
        expandedHeight: 'calc(100vh - 24px)',
        expandedMinHeight: 420,
        expandedMaxHeight: 'calc(100vh - 24px)',
        expandedInset: 12,
        normalRadius: 14,
        expandedRadius: 16,
      };
    }

    if (isTv) {
      return {
        collapsedHeight: 'clamp(420px, 32vh, 560px)',
        collapsedMinHeight: 420,
        collapsedMaxHeight: 560,
        expandedHeight: '86vh',
        expandedMinHeight: 560,
        expandedMaxHeight: '92vh',
        expandedInset: 24,
        normalRadius: 20,
        expandedRadius: 24,
      };
    }

    return {
      collapsedHeight: 'clamp(320px, 34vh, 440px)',
      collapsedMinHeight: 320,
      collapsedMaxHeight: 440,
      expandedHeight: '74vh',
      expandedMinHeight: 460,
      expandedMaxHeight: '84vh',
      expandedInset: 16,
      normalRadius: 16,
      expandedRadius: 18,
    };
  }, [isPhone, isTablet, isTv]);

  const resolvedIsTouchViewport = isTouchViewport ?? (isPhone || isTablet);
  const resolvedCollapsedHeight = collapsedHeight ?? viewportConfig.collapsedHeight;
  const resolvedCollapsedMinHeight = collapsedMinHeight ?? viewportConfig.collapsedMinHeight;
  const resolvedCollapsedMaxHeight = collapsedMaxHeight ?? viewportConfig.collapsedMaxHeight;
  const resolvedExpandedHeight = expandedHeight ?? viewportConfig.expandedHeight;
  const resolvedExpandedMinHeight = expandedMinHeight ?? viewportConfig.expandedMinHeight;
  const resolvedExpandedMaxHeight = expandedMaxHeight ?? viewportConfig.expandedMaxHeight;
  const resolvedExpandedInset = expandedInset ?? viewportConfig.expandedInset;
  const resolvedExpandedTopOffset = expandedTopOffset ?? resolvedExpandedInset;
  const resolvedExpandedLeftOffset = expandedLeftOffset ?? resolvedExpandedInset;
  const resolvedExpandedRightOffset = expandedRightOffset ?? resolvedExpandedInset;
  const resolvedExpandedBottomOffset = expandedBottomOffset ?? resolvedExpandedInset;
  const resolvedNormalRadius = normalRadius ?? viewportConfig.normalRadius;
  const resolvedExpandedRadius = expandedRadius ?? viewportConfig.expandedRadius;
  const resolvedZIndex = Number.isFinite(zIndex) ? zIndex : 1300;

  useEffect(() => {
    if (!show) {
      setExpanded(false);
    }
  }, [show]);

  useEffect(() => {
    if (collapseSignal == null) {
      return;
    }
    setExpanded(false);
  }, [collapseSignal]);

  useEffect(() => {
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  useEffect(() => {
    const viewportType = isPhone ? 'phone' : isTablet ? 'tablet' : isTv ? 'tv' : 'desktop';

    if (previousViewportTypeRef.current == null) {
      previousViewportTypeRef.current = viewportType;
      return;
    }

    if (previousViewportTypeRef.current !== viewportType) {
      setExpanded(false);
    }

    previousViewportTypeRef.current = viewportType;
  }, [isPhone, isTablet, isTv]);

  useEffect(() => {
    if (!expanded) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    bodyScrollPositionRef.current = window.scrollY;

    const originalBody = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overscrollBehavior: document.body.style.overscrollBehavior,
    };

    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${bodyScrollPositionRef.current}px`;
    document.body.style.width = '100%';
    document.body.style.overscrollBehavior = 'contain';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = originalBody.overflow;
      document.body.style.position = originalBody.position;
      document.body.style.top = originalBody.top;
      document.body.style.width = originalBody.width;
      document.body.style.overscrollBehavior = originalBody.overscrollBehavior;
      document.documentElement.style.overflow = originalHtmlOverflow;
      window.scrollTo({ top: bodyScrollPositionRef.current, behavior: 'auto' });
    };
  }, [expanded]);

  useEffect(() => {
    const triggerMapResize = () => {
      if (!map || typeof map.resize !== 'function') {
        return;
      }
      if (!map.getContainer()?.isConnected) {
        return;
      }

      requestAnimationFrame(() => {
        map.resize();
      });
    };

    triggerMapResize();

    const panel = panelRef.current;
    if (!panel) {
      return undefined;
    }

    const handleTransitionEnd = () => triggerMapResize();
    panel.addEventListener('transitionend', handleTransitionEnd);

    const observer = window.ResizeObserver
      ? new ResizeObserver(() => {
        triggerMapResize();
      })
      : null;

    observer?.observe(panel);
    window.addEventListener('resize', triggerMapResize);

    return () => {
      panel.removeEventListener('transitionend', handleTransitionEnd);
      observer?.disconnect();
      window.removeEventListener('resize', triggerMapResize);
    };
  }, [expanded, show, resolvedExpandedHeight, resolvedCollapsedHeight]);

  const handleToggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handleExpandButtonKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleExpanded();
    }
  }, [handleToggleExpanded]);

  if (!show) {
    return null;
  }

  const mapInteractive = expanded || !resolvedIsTouchViewport;
  const expandLabel = expanded ? t('sharedCollapse') : t('sharedExpand');
  const clearSelectionLabel = t('sharedClearSelection') || t('sharedClear');
  const floatingMenuBottom = expanded && resolvedIsTouchViewport
    ? 'calc(12px + env(safe-area-inset-bottom, 0px))'
    : 12;
  const expandedBottom = expanded && resolvedIsTouchViewport
    ? `calc(${resolvedExpandedBottomOffset}px + env(safe-area-inset-bottom, 0px))`
    : resolvedExpandedBottomOffset;

  return (
    <>
      {expanded && (
        <div
          className="reportMapPanelBackdrop"
          onClick={handleToggleExpanded}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.32)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            zIndex: resolvedZIndex - 1,
          }}
        />
      )}

      <div
        ref={panelRef}
        style={{
          height: expanded ? resolvedExpandedHeight : resolvedCollapsedHeight,
          minHeight: expanded ? resolvedExpandedMinHeight : resolvedCollapsedMinHeight,
          maxHeight: expanded ? resolvedExpandedMaxHeight : resolvedCollapsedMaxHeight,
          position: expanded ? 'fixed' : 'relative',
          top: expanded ? resolvedExpandedTopOffset : 'auto',
          left: expanded ? resolvedExpandedLeftOffset : 'auto',
          right: expanded ? resolvedExpandedRightOffset : 'auto',
          bottom: expanded ? expandedBottom : 'auto',
          zIndex: expanded ? resolvedZIndex : 'auto',
          background: '#fff',
          borderRadius: expanded ? resolvedExpandedRadius : resolvedNormalRadius,
          overflow: 'hidden',
          boxShadow: expanded
            ? '0 24px 80px rgba(15, 23, 42, 0.24)'
            : '0 10px 32px rgba(15, 23, 42, 0.10)',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          overscrollBehavior: expanded ? 'contain' : 'auto',
          WebkitOverflowScrolling: expanded ? 'touch' : 'auto',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: mapInteractive ? 'auto' : 'none',
            touchAction: mapInteractive ? 'auto' : 'pan-y',
            overscrollBehavior: mapInteractive ? 'contain' : 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
          <MapScale />
        </div>

        <div
          style={{
            position: 'absolute',
            left: 12,
            right: resolvedIsTouchViewport ? 12 : 'auto',
            bottom: floatingMenuBottom,
            zIndex: resolvedZIndex + 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: resolvedIsTouchViewport ? 'space-between' : 'flex-start',
            gap: 8,
            background: 'rgba(255, 255, 255, 0.96)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            borderRadius: 999,
            padding: '8px 10px 8px 12px',
            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.16)',
            maxWidth: resolvedIsTouchViewport ? 'calc(100% - 24px)' : 'unset',
            pointerEvents: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
              color: '#334155',
              paddingLeft: 2,
              minWidth: 0,
            }}
          >
            <MapIcon style={{ fontSize: 16 }} />
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </span>
          </div>

          <div
            style={{
              width: 1,
              height: 18,
              background: 'rgba(15, 23, 42, 0.10)',
              marginLeft: 2,
              marginRight: 2,
              display: resolvedIsTouchViewport ? 'none' : 'block',
            }}
          />

          {Boolean(onClearSelection) && !expanded && (
            <Tooltip title={clearSelectionLabel}>
              <IconButton
                size="small"
                onClick={onClearSelection}
                aria-label={clearSelectionLabel}
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: 'rgba(15, 23, 42, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(15, 23, 42, 0.08)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #2563eb',
                    outlineOffset: 2,
                  },
                }}
              >
                <GpsFixedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={expandLabel}>
            <IconButton
              size="small"
              onClick={handleToggleExpanded}
              onKeyDown={handleExpandButtonKeyDown}
              aria-label={expandLabel}
              sx={{
                width: 32,
                height: 32,
                backgroundColor: 'rgba(15, 23, 42, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.08)',
                },
                '&:focus-visible': {
                  outline: '2px solid #2563eb',
                  outlineOffset: 2,
                },
              }}
            >
              {expanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </>
  );
};

ReportMapPanel.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  show: PropTypes.bool,
  onClearSelection: PropTypes.func,
  isTouchViewport: PropTypes.bool,
  collapsedHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  collapsedMinHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  collapsedMaxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  expandedHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  expandedMinHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  expandedMaxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  expandedInset: PropTypes.number,
  expandedTopOffset: PropTypes.number,
  expandedLeftOffset: PropTypes.number,
  expandedRightOffset: PropTypes.number,
  expandedBottomOffset: PropTypes.number,
  normalRadius: PropTypes.number,
  expandedRadius: PropTypes.number,
  zIndex: PropTypes.number,
  onExpandedChange: PropTypes.func,
  collapseSignal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

ReportMapPanel.defaultProps = {
  title: '',
  show: false,
  onClearSelection: null,
  isTouchViewport: null,
  collapsedHeight: null,
  collapsedMinHeight: null,
  collapsedMaxHeight: null,
  expandedHeight: null,
  expandedMinHeight: null,
  expandedMaxHeight: null,
  expandedInset: null,
  expandedTopOffset: null,
  expandedLeftOffset: null,
  expandedRightOffset: null,
  expandedBottomOffset: null,
  normalRadius: null,
  expandedRadius: null,
  zIndex: 1300,
  onExpandedChange: null,
  collapseSignal: null,
};

export default ReportMapPanel;
